import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { CalendarOff, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import api, { formatApiError } from "../api";
import i18n from "../i18n";
import { useAuth, can } from "../auth";
import { Button, Toast } from "../components/common";
import { useViewPrefs } from "../hooks/useViewPrefs";
import { useHasFinePointer, useIsDesktop } from "../hooks/useMediaQuery";
import CalendarMonthGrid from "../components/CalendarMonthGrid";
import CalendarTimeGrid from "../components/CalendarTimeGrid";
import CalendarEventPopover from "../components/CalendarEventPopover";
import CalendarEventModal, { draftForSlot, draftFromEvent } from "../components/CalendarEventModal";
import {
  MINUTES_PER_DAY, VIEWS, dateAtMinutes, minutesOfDay,
  rangeLabel, shiftCursor, startOfDay, visibleDays,
} from "../utils/calendarDates";
import { EVENT_TYPES, bucketByDay, buildEvents, filterEvents, typeStyle } from "../utils/calendarEvents";

// Newest-first, and only to give meetings a real end time and a link target --
// the activities feed is the calendar's actual source of truth. A meeting older
// than this window still renders (from its anchor activity), just with the
// default one-hour block instead of its own duration.
const MEETING_WINDOW = 300;
const SNAP = 30;

const PREF_KEY = "wsai.calendar.view";
const PREF_OPTIONS = {
  view: VIEWS,
  // Predicates rather than allow-lists: the valid set is every subset of the
  // five types, and a boolean isn't enumerable as a list of values.
  types: (v) => Array.isArray(v) && v.every((x) => EVENT_TYPES.includes(x)),
  hideCompleted: (v) => typeof v === "boolean",
};
const PREF_DEFAULTS = { view: "month", types: EVENT_TYPES, hideCompleted: false };

export default function Calendar() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const writable = can.write(user);
  const isDesktop = useIsDesktop();
  const finePointer = useHasFinePointer();
  // HTML5 drag-and-drop never fires on touch, and on a touch calendar the drag
  // gesture is a scroll -- so reschedule-by-drag is a pointer-device feature,
  // with the edit form as the path that works everywhere.
  const canDrag = writable && finePointer;

  const [prefs, setPrefs] = useViewPrefs(PREF_KEY, PREF_OPTIONS, PREF_DEFAULTS);
  const [cursor, setCursor] = useState(() => startOfDay(new Date()));
  const [today, setToday] = useState(() => startOfDay(new Date()));
  const [direction, setDirection] = useState(0);

  const [activities, setActivities] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [toast, setToast] = useState(null);

  const [popover, setPopover] = useState(null);   // {event, anchor}
  const [draft, setDraft] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const dragRef = useRef(null);
  const [dragging, setDragging] = useState(null);

  // Guests are blocked from /meetings by role (a summary can carry money
  // figures), so asking would be a guaranteed 403 in their console.
  const canSeeMeetings = !!user && user.role !== "guest";

  const load = useCallback(async () => {
    try {
      const [act, meet] = await Promise.all([
        api.get("/activities"),
        canSeeMeetings
          ? api.get("/meetings", { params: { limit: MEETING_WINDOW } }).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
      ]);
      setActivities(act.data);
      setMeetings(meet.data);
    } catch (e) {
      setActivities([]);
      setToast({ type: "error", message: formatApiError(e.response?.data?.detail) || e.message });
    }
  }, [canSeeMeetings]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/contacts").then((r) => setContacts(r.data)).catch(() => setContacts([])); }, []);

  // "Today" is state, not a render-time new Date(): a tab left open overnight
  // would otherwise keep highlighting yesterday.
  useEffect(() => {
    const id = setInterval(() => {
      setToday((prev) => {
        const now = startOfDay(new Date());
        return +now === +prev ? prev : now;
      });
    }, 60000);
    return () => clearInterval(id);
  }, []);

  const events = useMemo(() => buildEvents(activities || [], meetings), [activities, meetings]);
  const visible = useMemo(() => filterEvents(events, prefs), [events, prefs]);
  const eventsByDay = useMemo(() => bucketByDay(visible), [visible]);
  const days = useMemo(() => visibleDays(prefs.view, cursor), [prefs.view, cursor]);
  const undated = useMemo(
    () => (activities || []).filter((a) => !a.due_date).length,
    [activities]
  );
  const contactNames = useMemo(() => {
    const map = new Map();
    for (const c of contacts) map.set(c.id, `${c.first_name} ${c.last_name}`.trim());
    return map;
  }, [contacts]);
  const hiddenCount = events.length - visible.length;

  // ---------- navigation ----------
  const go = useCallback((dir) => {
    setDirection(dir);
    setCursor((c) => shiftCursor(prefs.view, c, dir));
    setPopover(null);
  }, [prefs.view]);

  const goToday = useCallback(() => {
    setDirection(0);
    setCursor(startOfDay(new Date()));
    setPopover(null);
  }, []);

  const setView = useCallback((view) => {
    setDirection(0);
    setPopover(null);
    setPrefs((p) => ({ ...p, view }));
  }, [setPrefs]);

  const openDay = useCallback((date) => {
    setDirection(0);
    setPopover(null);
    setCursor(startOfDay(date));
    setPrefs((p) => ({ ...p, view: "day" }));
  }, [setPrefs]);

  // ---------- create / edit ----------
  const openCreate = useCallback((day, minutes) => {
    if (!writable) return;
    setPopover(null);
    setFormError(null);
    setDraft(draftForSlot(day, minutes));
    setModalOpen(true);
  }, [writable]);

  const openEdit = useCallback((event) => {
    setPopover(null);
    setFormError(null);
    setDraft(draftFromEvent(event));
    setModalOpen(true);
  }, []);

  const closeModal = () => { setModalOpen(false); setSaving(false); };

  const saveDraft = async () => {
    const start = new Date(`${draft.date}T${draft.allDay ? "00:00" : draft.startTime}`);
    if (Number.isNaN(start.getTime())) {
      setFormError(t("calendar.dateRequired"));
      return;
    }
    const end = draft.allDay ? null : new Date(`${draft.date}T${draft.endTime}`);
    const iso = (d) => (d ? d.toISOString() : null);
    const contactId = draft.contact_id || null;

    setSaving(true);
    setFormError(null);
    try {
      if (draft.meetingId) {
        // The meetings router owns starts_at and mirrors it onto
        // Activity.due_date; writing the activity directly would leave
        // Meeting.starts_at stale and the meeting page showing the old time.
        await api.put(`/meetings/${draft.meetingId}`, {
          subject: draft.subject.trim(),
          description: draft.description || null,
          starts_at: iso(start),
          ends_at: iso(end),
          contact_id: contactId,
        });
      } else if (!draft.id && draft.type === "meeting") {
        await api.post("/meetings", {
          subject: draft.subject.trim(),
          description: draft.description || null,
          starts_at: iso(start),
          ends_at: iso(end),
          contact_id: contactId,
        });
      } else {
        const base = draft.activity || {};
        const payload = {
          type: draft.type,
          direction: base.direction || null,
          subject: draft.subject.trim(),
          description: draft.description || null,
          due_date: iso(start),
          completed: !!base.completed,
          contact_id: contactId,
          company_id: base.company_id || null,
          deal_id: base.deal_id || null,
          project_id: base.project_id || null,
        };
        if (draft.id) await api.put(`/activities/${draft.id}`, payload);
        else await api.post("/activities", payload);
      }
      setModalOpen(false);
      setToast({ type: "success", message: t("calendar.saved") });
      await load();
    } catch (e) {
      setFormError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  // ---------- mutations from the popover ----------
  const toggleEvent = async (event) => {
    const snapshot = activities;
    setActivities((list) =>
      list.map((a) => (a.id === event.id ? { ...a, completed: !a.completed } : a))
    );
    setPopover(null);
    try {
      await api.patch(`/activities/${event.id}/toggle`);
    } catch (e) {
      setActivities(snapshot);
      setToast({ type: "error", message: formatApiError(e.response?.data?.detail) || e.message });
    }
  };

  const deleteEvent = async (event) => {
    if (!window.confirm(t("common.confirmDelete"))) return;
    setPopover(null);
    try {
      // Deleting the anchor activity takes the meeting with it (CASCADE), so
      // there's one delete path for both kinds of event.
      await api.delete(`/activities/${event.id}`);
      setToast({ type: "success", message: t("calendar.deleted") });
      await load();
    } catch (e) {
      setToast({ type: "error", message: formatApiError(e.response?.data?.detail) || e.message });
    }
  };

  // ---------- drag to reschedule ----------
  const handleDragStart = (event, grabbedMinutes, e) => {
    dragRef.current = { event, grabbedMinutes };
    setDragging(event);
    setPopover(null);
    // Firefox refuses to start a drag without payload on the transfer object.
    try { e.dataTransfer.setData("text/plain", event.id); } catch { /* ignore */ }
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => { dragRef.current = null; setDragging(null); };

  const reschedule = async (event, newStart, allDay) => {
    if (+newStart === +event.start && allDay === event.allDay) return;
    const duration = allDay || event.allDay ? 0 : event.end - event.start;
    const newEnd = duration > 0 ? new Date(+newStart + duration) : null;
    const iso = (d) => (d ? d.toISOString() : null);

    const actSnapshot = activities;
    const meetSnapshot = meetings;
    setActivities((list) =>
      list.map((a) => (a.id === event.id ? { ...a, due_date: iso(newStart) } : a))
    );
    if (event.meetingId) {
      setMeetings((list) =>
        list.map((m) => (m.id === event.meetingId
          ? { ...m, starts_at: iso(newStart), ends_at: iso(newEnd) }
          : m))
      );
    }

    try {
      if (event.meetingId) {
        await api.put(`/meetings/${event.meetingId}`, { starts_at: iso(newStart), ends_at: iso(newEnd) });
      } else {
        const a = event.activity;
        await api.put(`/activities/${event.id}`, {
          type: a.type, direction: a.direction || null, subject: a.subject,
          description: a.description || null, due_date: iso(newStart),
          completed: !!a.completed, contact_id: a.contact_id || null,
          company_id: a.company_id || null, deal_id: a.deal_id || null,
          project_id: a.project_id || null,
        });
      }
      setToast({ type: "success", message: t("calendar.moved") });
    } catch (e) {
      setActivities(actSnapshot);
      setMeetings(meetSnapshot);
      setToast({ type: "error", message: formatApiError(e.response?.data?.detail) || e.message });
    }
  };

  /** Time grid: dropped on `day` at `minutes` (null = the all-day row). */
  const handleDropAt = (day, minutes) => {
    const d = dragRef.current;
    handleDragEnd();
    if (!d) return;
    if (minutes == null) {
      reschedule(d.event, dateAtMinutes(day, 0), true);
      return;
    }
    const raw = minutes - d.grabbedMinutes;
    const snapped = Math.min(
      MINUTES_PER_DAY - SNAP,
      Math.max(0, Math.round(raw / SNAP) * SNAP)
    );
    reschedule(d.event, dateAtMinutes(day, snapped), false);
  };

  /** Month grid: only the date changes, the time of day is carried over. */
  const handleDropOnDay = (day) => {
    const d = dragRef.current;
    handleDragEnd();
    if (!d) return;
    const keep = d.event.allDay ? 0 : minutesOfDay(d.event.start);
    reschedule(d.event, dateAtMinutes(day, keep), d.event.allDay);
  };

  // ---------- keyboard ----------
  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target;
      const tag = el?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el?.isContentEditable) return;
      if (modalOpen) return;
      const keys = {
        ArrowLeft: () => go(-1), ArrowRight: () => go(1),
        t: goToday, m: () => setView("month"), w: () => setView("week"),
        x: () => setView("day3"), d: () => setView("day"),
        n: () => writable && openCreate(cursor, null),
      };
      const fn = keys[e.key] || keys[e.key?.toLowerCase()];
      if (!fn) return;
      e.preventDefault();
      fn();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, goToday, setView, openCreate, cursor, modalOpen, writable]);

  const label = rangeLabel(prefs.view, cursor, i18n.language);
  const gridProps = {
    days, eventsByDay, today, writable, canDrag, dragging,
    onOpenDay: openDay, onCreateAt: openCreate,
    onOpenEvent: (event, anchor) => setPopover({ event, anchor }),
    onDragStart: handleDragStart, onDragEnd: handleDragEnd,
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{t("calendar.title")}</h1>
        {writable && (
          <Button onClick={() => openCreate(cursor, null)} data-testid="cal-new-btn" title={`${t("calendar.newEvent")} (N)`}>
            <Plus size={16} />
            <span className="hidden sm:inline">{t("calendar.newEvent")}</span>
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex items-center">
            <button
              data-testid="cal-prev"
              onClick={() => go(-1)}
              aria-label={t("calendar.previous")}
              title={`${t("calendar.previous")} (←)`}
              className="p-2 rounded-l-sm border border-border text-muted hover:bg-surface hover:text-txt outline-none focus-visible:ring-2 focus-visible:ring-primary/60 transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              data-testid="cal-today"
              onClick={goToday}
              title={`${t("calendar.today")} (T)`}
              className="px-3 py-2 text-xs font-medium border-y border-border text-txt hover:bg-surface outline-none focus-visible:ring-2 focus-visible:ring-primary/60 transition-colors"
            >
              {t("calendar.today")}
            </button>
            <button
              data-testid="cal-next"
              onClick={() => go(1)}
              aria-label={t("calendar.next")}
              title={`${t("calendar.next")} (→)`}
              className="p-2 rounded-r-sm border border-border text-muted hover:bg-surface hover:text-txt outline-none focus-visible:ring-2 focus-visible:ring-primary/60 transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          {/* Keyed so a navigation reads as a directional swap of the range,
              which is the only place this page spends motion on content. */}
          <h2
            key={label}
            aria-live="polite"
            className={`font-display font-bold first-letter:uppercase truncate ${
              direction > 0 ? "animate-swap-next" : direction < 0 ? "animate-swap-prev" : "animate-fade-up"
            }`}
          >
            {label}
          </h2>
        </div>

        <ViewSwitcher view={prefs.view} onChange={setView} t={t} />
      </div>

      <FilterBar prefs={prefs} setPrefs={setPrefs} hiddenCount={hiddenCount} t={t} />

      {activities === null ? (
        <CalendarSkeleton view={prefs.view} />
      ) : prefs.view === "month" ? (
        <CalendarMonthGrid {...gridProps} cursor={cursor} onDropOnDay={handleDropOnDay} />
      ) : (
        <CalendarTimeGrid {...gridProps} onDropAt={handleDropAt} />
      )}

      {activities !== null && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted">
          {events.length === 0 && (
            <span className="inline-flex items-center gap-1.5">
              <CalendarOff size={13} />
              {writable ? t("calendar.emptyHint") : t("calendar.emptyHintReadOnly")}
            </span>
          )}
          {undated > 0 && (
            <span>
              {t("calendar.undated", { count: undated })}{" "}
              <Link to="/activities" className="text-primary hover:underline">
                {t("activity.title")}
              </Link>
            </span>
          )}
          {canDrag && events.length > 0 && <span>{t("calendar.dragHint")}</span>}
        </div>
      )}

      {popover && (
        <CalendarEventPopover
          event={popover.event}
          anchor={popover.anchor}
          isDesktop={isDesktop}
          writable={writable}
          contactName={contactNames.get(popover.event.activity.contact_id) || null}
          onClose={() => setPopover(null)}
          onToggle={toggleEvent}
          onEdit={openEdit}
          onDelete={deleteEvent}
        />
      )}

      <CalendarEventModal
        open={modalOpen}
        draft={draft}
        contacts={contacts}
        saving={saving}
        error={formError}
        onChange={setDraft}
        onClose={closeModal}
        onSubmit={saveDraft}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}

function ViewSwitcher({ view, onChange, t }) {
  const index = Math.max(0, VIEWS.indexOf(view));
  return (
    <div
      role="tablist"
      aria-label={t("calendar.viewLabel")}
      data-testid="cal-view-switcher"
      className="relative flex p-0.5 rounded-sm border border-border bg-surface"
    >
      {/* One pill that slides, instead of four backgrounds toggling: the
          movement is what makes the switch read as one control. */}
      <span
        aria-hidden="true"
        className="absolute top-0.5 bottom-0.5 rounded-sm bg-bg border border-border shadow-sm transition-[left] duration-200 ease-out"
        style={{ width: `calc(${100 / VIEWS.length}% - 2px)`, left: `calc(${(index * 100) / VIEWS.length}% + 1px)` }}
      />
      {VIEWS.map((v) => (
        <button
          key={v}
          role="tab"
          aria-selected={view === v}
          onClick={() => onChange(v)}
          data-testid={`cal-view-${v}`}
          title={`${t(`calendar.view.${v}`)} (${t(`calendar.viewKey.${v}`)})`}
          className={`relative flex-1 px-2.5 sm:px-3.5 py-1.5 text-xs font-medium rounded-sm whitespace-nowrap outline-none
            transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-primary/60
            ${view === v ? "text-txt" : "text-muted hover:text-txt"}`}
        >
          {t(`calendar.view.${v}`)}
        </button>
      ))}
    </div>
  );
}

function FilterBar({ prefs, setPrefs, hiddenCount, t }) {
  const toggleType = (type) => {
    setPrefs((p) => {
      const active = new Set(p.types);
      if (active.has(type)) active.delete(type);
      else active.add(type);
      // Never let the filter empty the calendar with no way back other than
      // guessing which chip to press: the last active type stays on.
      if (active.size === 0) return p;
      return { ...p, types: EVENT_TYPES.filter((x) => active.has(x)) };
    });
  };

  return (
    // Wraps rather than scrolls horizontally: at 390px the row overflows by a
    // chip, and a scroll container with no visible affordance simply hides
    // "Hide done" from anyone who doesn't think to swipe it.
    <div className="flex flex-wrap items-center gap-2" data-testid="cal-filters">
      {EVENT_TYPES.map((type) => {
        const style = typeStyle(type);
        const active = prefs.types.includes(type);
        return (
          <button
            key={type}
            onClick={() => toggleType(type)}
            aria-pressed={active}
            data-testid={`cal-filter-${type}`}
            className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border text-xs font-medium outline-none
              transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-primary/60
              ${active ? style.tint : "border-border text-muted hover:bg-surface hover:text-txt"}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${active ? style.dot : "bg-muted opacity-40"}`} />
            {t(`statuses.${type}`)}
          </button>
        );
      })}

      <span className="w-px h-5 bg-border shrink-0 mx-0.5" />

      <button
        onClick={() => setPrefs((p) => ({ ...p, hideCompleted: !p.hideCompleted }))}
        aria-pressed={prefs.hideCompleted}
        data-testid="cal-hide-completed"
        className={`shrink-0 px-2.5 py-1 rounded-sm border text-xs font-medium outline-none
          transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-primary/60
          ${prefs.hideCompleted
            ? "bg-primary/15 border-primary/30 text-primary"
            : "border-border text-muted hover:bg-surface hover:text-txt"}`}
      >
        {t("calendar.hideCompleted")}
      </button>

      {hiddenCount > 0 && (
        <span className="shrink-0 text-xs text-muted">{t("calendar.hidden", { count: hiddenCount })}</span>
      )}
    </div>
  );
}

function CalendarSkeleton({ view }) {
  // Mirrors the real grid's shape so the page doesn't jump when data lands.
  const cells = view === "month" ? 42 : 0;
  return (
    <div className="border border-border rounded-sm overflow-hidden animate-pulse" aria-hidden="true">
      <div className="grid grid-cols-7 border-b border-border bg-surface h-8" />
      {cells ? (
        <div className="grid grid-cols-7">
          {Array.from({ length: cells }, (_, i) => (
            <div key={i} className="min-h-[76px] sm:min-h-[104px] border-r border-b border-border p-2">
              <div className="w-5 h-3 rounded-sm bg-border" />
              {i % 3 === 0 && <div className="mt-2 h-3 rounded-sm bg-border" />}
            </div>
          ))}
        </div>
      ) : (
        <div className="h-[62vh] min-h-[24rem] flex">
          <div className="w-14 sm:w-16 border-r border-border" />
          <div className="flex-1 space-y-6 p-3">
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="h-3 rounded-sm bg-border" style={{ width: `${60 + ((i * 13) % 35)}%` }} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
